import React, {useState} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Expander from '@nib-components/expander';
import FocusTrap from 'focus-trap-react';
import {focusOutlineColor, focusOutlineStyle, focusOutlineWidth, copyLineHeight, colorLightest, colorWhite, copyFontSizeSmall, linkLoud} from '@nib-components/theme';
import Overlay from '@nib-components/overlay';
import {CardSystemIcon, ChevronDownSystemIcon, CloseSystemIcon, IconType} from '@nib/icons';
import {Box, Columns, Column, Inline, Hidden, Padding, breakpoint, m, p, pl} from '@nib/layout';
import Link from '@nib-components/link';
import {UnstyledButton} from '@nib-components/button';

export const DrawerWrapper = styled.div<{isOpen?: boolean}>`
  background: ${colorWhite};
  height: 100%;
  right: 3.5rem;
  z-index: 11;
  position: fixed;
  top: 0;
  left: 0;
  overflow: auto;
  transition: transform 0.3s ease-in-out;
  transform: ${({isOpen}) => (isOpen ? 'translateX(0%)' : 'translateX(-100%)')};

  ${breakpoint('xs', 'md')`
    right: 0;
  `}
`;

const ScreenReaderHiddenWhenClosed = styled.div<{isOpen?: boolean}>`
  display: ${({isOpen}) => (isOpen ? 'block' : 'none')};
`;

export const InvisibleButton = styled(props => <UnstyledButton {...props} padding={4} />)`
  background: ${colorWhite};
`;

const DrawerOverlay = styled(Overlay)`
  z-index: 9;
`;

export const HeaderLink = styled.a`
  color: ${linkLoud};
  font-weight: bold;
  text-decoration: none;

  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: ${focusOutlineWidth};
  }
`;

export const DrawerHeader = styled(Columns)`
  border-bottom: 1px solid ${colorLightest};
`;

const DrawerHeading = styled.div`
  font-weight: bold;
  line-height: 1;
`;

export const QuoteIcon = styled(CardSystemIcon)`
  vertical-align: sub;
`;

const ExpanderButton = styled(InvisibleButton)`
  &&& {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    border: none;
    background: none;
    color: ${linkLoud};
    ${p(4)};
    line-height: ${copyLineHeight};
    transform: none !important; // this fixes the bug, but is a band-aid fix
  }
  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: -${focusOutlineWidth};
  }
`;

const Chevron = styled(({expanded, ...otherProps}) => <ChevronDownSystemIcon {...otherProps} />)`
  ${({expanded}) => expanded && `transform: rotate(180deg);`};
  transition: transform 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
`;

const ExpanderContent = styled.ul`
  ${m(0)};
  ${p(0)};
  list-style: none;
`;

export const ExpanderLink = styled(Link).attrs({underline: false})`
  &&& {
    box-sizing: border-box;
    display: block;
    width: 100%;
    ${p(4)};
    text-align: left;
    color: inherit;
  }

  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: -${focusOutlineWidth};
  }
`;

export const ExpanderSubLink = styled(ExpanderLink)`
  ${pl(6)};
`;

ExpanderSubLink.displayName = 'ExpanderSubLink';

const RibbonLink = styled(Link)`
  &&& {
    color: inherit;
    font-size: ${copyFontSizeSmall};
  }
`;

RibbonLink.displayName = 'RibbonLink';

export interface DrawerExpanderProps {
  link: {
    title: string;
    icon?: IconType;
  };
  children?: React.ReactNode;
}

export const DrawerExpander: React.FC<DrawerExpanderProps> = ({link, children}) => {
  const [isExpanded, setExpanded] = useState(false);
  const Icon = link.icon;
  return (
    <>
      <ExpanderButton title={link.title} aria-expanded={isExpanded} aria-haspopup="true" onClick={() => setExpanded(!isExpanded)}>
        <LineHeightZeroInline space={4} verticalAlign="center">
          {Icon ? <Icon size="md" /> : ''}
          <DrawerHeading>{link.title}</DrawerHeading>
        </LineHeightZeroInline>
        <Chevron expanded={isExpanded} />
      </ExpanderButton>
      <Expander expanded={isExpanded} aria-label={link.title}>
        <ExpanderContent>{children}</ExpanderContent>
      </Expander>
    </>
  );
};

DrawerExpander.displayName = 'DrawerExpander';

export const DrawerMenu = styled.ul`
  display: flex;
  flex-direction: column;
  ${m(0)};
  ${p(0)};
  list-style: none;
`;

export const MenuItem = styled.li`
  display: block;
  position: relative;

  &:not(:first-of-type) {
    border-top: 1px solid ${colorLightest};
  }

  &:last-of-type {
    border-bottom: 1px solid ${colorLightest};
  }
`;

export const MenuLink = styled.a`
  box-sizing: border-box;
  appearance: none;
  border: none;
  background: none;

  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  ${p(4)};
  text-decoration: none;
  color: ${linkLoud};
  font-weight: bold;

  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: -${focusOutlineWidth};
  }
`;

const LineHeightZeroInline = styled(Inline)`
  line-height: 0;
`;

const LineHeightOneInline = styled(Inline)`
  line-height: 1;
`;

export interface LinkType {
  id: string;
  title: string;
  url?: string;
}

export interface ChildLinkType extends LinkType {
  targetBlank?: boolean;
  children?: ChildLinkType[];
  [key: string]: unknown;
}

export interface NavLinksType extends LinkType {
  targetBlank?: boolean;
  hideInDrawer?: boolean;
  label?: string;
  icon?: IconType;
  children?: ChildLinkType[];
  [key: string]: unknown;
}

export interface DrawerContentProps {
  navLinks?: NavLinksType[];
  ribbonLinks?: LinkType[];
  navSlot?: React.ReactNode;
  children?: React.ReactNode;
}

const DrawerContent: React.FC<DrawerContentProps> = ({navLinks, ribbonLinks, navSlot, children}) => {
  return (
    <>
      <DrawerMenu>
        {navLinks &&
          navLinks.length > 0 &&
          navLinks
            .filter(link => !link.hideInDrawer)
            .map(link => {
              const {id, url, title, icon, targetBlank, children, ...otherProps} = link;
              const Icon = icon;
              return link.children ? (
                <MenuItem key={id}>
                  <DrawerExpander link={link}>
                    {url && (
                      <li key={`${id}-mobile`}>
                        <ExpanderLink id={`${id}-mobile`} title={title} href={url} target={targetBlank ? '_blank' : ''} rel={targetBlank ? 'noopener noreferrer' : ''} {...otherProps}>
                          {title}
                        </ExpanderLink>
                      </li>
                    )}
                    {children &&
                      children.map(child => {
                        const {id, url, title, targetBlank, ...otherProps} = child;
                        return (
                          <li key={`${id}-mobile`}>
                            <ExpanderLink id={`${id}-mobile`} title={title} href={url} target={targetBlank ? '_blank' : ''} rel={targetBlank ? 'noopener noreferrer' : ''} {...otherProps}>
                              {title}
                            </ExpanderLink>

                            {child.children && (
                              <DrawerMenu>
                                {child.children &&
                                  child.children.map(subChild => {
                                    const {id, url, title, targetBlank, ...otherProps} = subChild;
                                    return (
                                      <li key={`${id}-mobile`}>
                                        <ExpanderSubLink
                                          id={`${id}-mobile`}
                                          title={title}
                                          href={url}
                                          target={targetBlank ? '_blank' : ''}
                                          rel={targetBlank ? 'noopener noreferrer' : ''}
                                          {...otherProps}
                                        >
                                          <Inline verticalAlign="center" space={2}>
                                            <Hidden screenReader>
                                              <Box color="dark">&mdash;</Box>
                                            </Hidden>
                                            <span>{title}</span>
                                          </Inline>
                                        </ExpanderSubLink>
                                      </li>
                                    );
                                  })}
                              </DrawerMenu>
                            )}
                          </li>
                        );
                      })}
                  </DrawerExpander>
                </MenuItem>
              ) : (
                <MenuItem key={id}>
                  <MenuLink id={`${id}-mobile`} title={title} href={url} target={targetBlank ? '_blank' : ''} rel={targetBlank ? 'noopener noreferrer' : ''} key={`${id}-mobile`} {...otherProps}>
                    <LineHeightZeroInline space={4} verticalAlign="center">
                      {Icon ? <Icon size="md" /> : ''}
                      <LineHeightOneInline>
                        <span>{title}</span>
                      </LineHeightOneInline>
                    </LineHeightZeroInline>
                  </MenuLink>
                </MenuItem>
              );
            })}
        {navSlot &&
          React.cloneElement(navSlot as React.ReactElement<any>, {
            isInDrawer: true
          })}
      </DrawerMenu>
      {ribbonLinks && ribbonLinks.length > 0 && (
        <Padding all={4}>
          <Inline space={3}>
            {ribbonLinks.map((link, i) => {
              const {id, url, title, ...otherProps} = link;
              return (
                <RibbonLink id={`${id}-mobile`} key={`item-${i}-${id}-mobile`} href={url} title={title} {...otherProps}>
                  {title}
                </RibbonLink>
              );
            })}
          </Inline>
        </Padding>
      )}
      {children && <Box padding={4}>{children}</Box>}
    </>
  );
};

DrawerContent.displayName = 'DrawerContent';

DrawerContent.propTypes = {
  navLinks: PropTypes.array,
  ribbonLinks: PropTypes.array,
  navSlot: PropTypes.node,
  children: PropTypes.node
};

export interface DrawerProps extends DrawerContentProps {
  isOpen?: boolean;
  setIsOpen: (arg0: boolean) => void;
  hideQuoteButton?: boolean;
  quoteButtonUrl?: string;
  [key: string]: unknown; // other props
}

const Drawer: React.FC<DrawerProps> & {DrawerWrapper: typeof DrawerWrapper} & {
  DrawerMenu: typeof DrawerMenu;
} = ({isOpen = false, setIsOpen, navLinks, ribbonLinks, hideQuoteButton, quoteButtonUrl, navSlot, children, ...otherProps}) => {
  const defaultedQuoteUrl = quoteButtonUrl ? quoteButtonUrl : '/health-insurance/quote/welcome';
  return (
    <>
      <DrawerOverlay visible={isOpen} />
      <FocusTrap
        active={isOpen}
        focusTrapOptions={{
          clickOutsideDeactivates: true,
          onDeactivate: () => setIsOpen(false)
        }}
      >
        <DrawerWrapper isOpen={isOpen} {...otherProps}>
          <ScreenReaderHiddenWhenClosed isOpen={isOpen}>
            <DrawerHeader>
              <Column>
                <Box padding={4} display="flex" justifyContent="center">
                  {!hideQuoteButton && (
                    <HeaderLink id="ga-nav-hi-quote-mobile" href={defaultedQuoteUrl} aria-label="Get a quote">
                      <Inline space={2} align="center">
                        <QuoteIcon />
                        <span>Get a quote</span>
                      </Inline>
                    </HeaderLink>
                  )}
                </Box>
              </Column>
              <Column width="content">
                <InvisibleButton onClick={() => setIsOpen(false)} aria-label="Close menu">
                  <CloseSystemIcon />
                </InvisibleButton>
              </Column>
            </DrawerHeader>
            <DrawerContent navLinks={navLinks} ribbonLinks={ribbonLinks} navSlot={navSlot}>
              {children}
            </DrawerContent>
          </ScreenReaderHiddenWhenClosed>
        </DrawerWrapper>
      </FocusTrap>
    </>
  );
};

Drawer.displayName = 'Drawer';
Drawer.DrawerWrapper = DrawerWrapper;
Drawer.DrawerMenu = DrawerMenu;

Drawer.propTypes = {
  setIsOpen: PropTypes.func.isRequired,
  navLinks: PropTypes.array.isRequired,
  isOpen: PropTypes.bool,
  ribbonLinks: PropTypes.array,
  hideQuoteButton: PropTypes.bool,
  quoteButtonUrl: PropTypes.string,
  navSlot: PropTypes.node,
  children: PropTypes.node
};

export default Drawer;
